import type { Types as AmplitudeTypes } from '@amplitude/analytics-browser';
import { getConsent } from '@/lib/consent';
import { clearWebStorage } from '@/lib/storage/browserStorage';
import { getString, setString } from '@/lib/storage/sessionStorage';
import { AD_TRACKING_CONSENT_EVENT_PROPERTY } from './constants';
import {
  hasSessionCampaignAttribution,
  SessionCampaignAttributionState,
  SESSION_UTM_CAMPAIGN_EVENT_PROPERTY,
  SESSION_UTM_MEDIUM_EVENT_PROPERTY,
  SESSION_UTM_SOURCE_EVENT_PROPERTY,
  type CampaignCapture,
  type SessionCampaignAttribution,
  type SessionCampaignStorage,
  type SessionCampaignTeardownReason,
  type StoredSessionCampaign,
} from './sessionCampaignAttributionState';

export {
  SESSION_UTM_CAMPAIGN_EVENT_PROPERTY,
  SESSION_UTM_MEDIUM_EVENT_PROPERTY,
  SESSION_UTM_SOURCE_EVENT_PROPERTY,
};
export type { SessionCampaignAttribution, SessionCampaignTeardownReason };

const STORAGE_KEY_PREFIX = 'ft-amplitude-session-campaign-v1:';
const PENDING_STORAGE_KEY_PREFIX = `${STORAGE_KEY_PREFIX}pending:`;
const CAMPAIGN_CAPTURE_KEYS = ['source', 'medium', 'campaign'] as const;
const attributionState = new SessionCampaignAttributionState();

const normalizeValue = (value: string | null | undefined): string | undefined => {
  const normalizedValue = value?.trim();
  return normalizedValue || undefined;
};

const getStorageKey = (sessionId: number): string => `${STORAGE_KEY_PREFIX}${sessionId}`;
const getPendingStorageKey = (key: keyof CampaignCapture): string =>
  `${PENDING_STORAGE_KEY_PREFIX}${key}`;

const clearStoredSessionsExcept = (sessionId?: number): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const retainedKey = sessionId === undefined ? null : getStorageKey(sessionId);
  clearWebStorage(
    () => window.sessionStorage,
    (key) => key.startsWith(STORAGE_KEY_PREFIX) && key !== retainedKey
  );
};

const parseStoredSession = (
  storedValue: string | null,
  expectedSessionId?: number
): StoredSessionCampaign | null => {
  if (!storedValue) {
    return null;
  }

  try {
    const storedSession = JSON.parse(storedValue) as Partial<StoredSessionCampaign>;
    if (
      storedSession.sessionId === undefined ||
      !Number.isFinite(storedSession.sessionId) ||
      storedSession.sessionId < 0 ||
      (expectedSessionId !== undefined && storedSession.sessionId !== expectedSessionId) ||
      !storedSession.attribution
    ) {
      return null;
    }

    const attribution: SessionCampaignAttribution = {
      [SESSION_UTM_SOURCE_EVENT_PROPERTY]: normalizeValue(
        storedSession.attribution[SESSION_UTM_SOURCE_EVENT_PROPERTY]
      ),
      [SESSION_UTM_MEDIUM_EVENT_PROPERTY]: normalizeValue(
        storedSession.attribution[SESSION_UTM_MEDIUM_EVENT_PROPERTY]
      ),
      [SESSION_UTM_CAMPAIGN_EVENT_PROPERTY]: normalizeValue(
        storedSession.attribution[SESSION_UTM_CAMPAIGN_EVENT_PROPERTY]
      ),
    };

    return hasSessionCampaignAttribution(attribution)
      ? { sessionId: storedSession.sessionId, attribution }
      : null;
  } catch {
    return null;
  }
};

const restoreAttribution = (sessionId: number): SessionCampaignAttribution => {
  if (!getConsent().allowed) {
    return {};
  }

  const storedSession = parseStoredSession(getString(getStorageKey(sessionId)), sessionId);
  if (!storedSession) {
    setString(getStorageKey(sessionId), null);
    return {};
  }

  return storedSession.attribution;
};

const findLatestAttribution = (): SessionCampaignAttribution => {
  if (typeof window === 'undefined' || !getConsent().allowed) {
    return {};
  }

  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(STORAGE_KEY_PREFIX) && !key.startsWith(PENDING_STORAGE_KEY_PREFIX)) {
        const storedSession = parseStoredSession(getString(key));
        if (storedSession) {
          return storedSession.attribution;
        }
      }
    }
  } catch {
    return {};
  }

  return {};
};

const restorePendingCapture = (): CampaignCapture | undefined => {
  if (!getConsent().allowed) {
    return undefined;
  }

  const capture = {
    source: normalizeValue(getString(getPendingStorageKey('source'))),
    medium: normalizeValue(getString(getPendingStorageKey('medium'))),
    campaign: normalizeValue(getString(getPendingStorageKey('campaign'))),
  };
  return capture.source || capture.medium || capture.campaign ? capture : undefined;
};

const persistPendingCapture = (capture: CampaignCapture): void => {
  if (!getConsent().allowed) {
    return;
  }

  CAMPAIGN_CAPTURE_KEYS.forEach((key) => {
    setString(getPendingStorageKey(key), normalizeValue(capture[key]) ?? null);
  });
};

const campaignStorage: SessionCampaignStorage = {
  clearExcept: clearStoredSessionsExcept,
  findLatest: findLatestAttribution,
  persist: (record) => {
    if (!getConsent().allowed || !hasSessionCampaignAttribution(record.attribution)) {
      return;
    }

    clearStoredSessionsExcept(record.sessionId);
    setString(getStorageKey(record.sessionId), JSON.stringify(record));
  },
  restore: restoreAttribution,
};

const getCampaignCaptureFromSearch = (search: string): CampaignCapture | undefined => {
  if (!search) {
    return undefined;
  }

  const params = new URLSearchParams(search);
  const capture = {
    source: normalizeValue(params.get('utm_source')),
    medium: normalizeValue(params.get('utm_medium')),
    campaign: normalizeValue(params.get('utm_campaign')),
  };

  return capture.source || capture.medium || capture.campaign ? capture : undefined;
};

export const captureSessionCampaignAttribution = (capture: CampaignCapture): void => {
  persistPendingCapture(capture);
  attributionState.capture(capture);
};

export const enrichSessionCampaignAttributionEvent = (
  event: AmplitudeTypes.Event,
  search: string
): AmplitudeTypes.Event => {
  const capture = getCampaignCaptureFromSearch(search) ?? restorePendingCapture();
  const attribution = attributionState.enrichEvent(
    {
      canEnrich: event.event_properties?.[AD_TRACKING_CONSENT_EVENT_PROPERTY] !== false,
      capture,
      eventType: event.event_type,
      sessionId: event.session_id,
    },
    campaignStorage
  );

  if (!hasSessionCampaignAttribution(attribution)) {
    return event;
  }

  return {
    ...event,
    event_properties: {
      ...event.event_properties,
      ...attribution,
    },
  };
};

export const teardownSessionCampaignAttribution = (reason: SessionCampaignTeardownReason): void => {
  attributionState.teardown(reason, campaignStorage);
};
